/**
 * Singleton app state with a simple subscriber model.
 *
 * Components register a render callback via `subscribe()`. Every `setState()`
 * merges the patch and schedules one render on the next animation frame —
 * multiple synchronous setState calls coalesce into a single repaint.
 */

import * as api from './api.js';
import * as enc from './encounter.js';
import type {
  AppState,
  EditMode,
  EncounterState,
  ExploredCells,
  MapOverlay,
  MapRecord,
  OverlayCell,
  Token,
  Viewport,
} from './types.js';

let state: AppState = {
  user: null,
  csrfToken: null,
  maps: [],
  currentMap: null,
  tokens: [],
  viewport: { x: 0, y: 0, zoom: 1 },
  draggingTokenId: null,
  editMode: 'tokens',
  overlaySubMode: 'walls',
};

type Subscriber = (state: AppState) => void;
const subscribers = new Set<Subscriber>();
let renderScheduled = false;

export function getState(): AppState {
  return state;
}

export function setState(patch: Partial<AppState>): void {
  state = { ...state, ...patch };
  scheduleRender();
}

export function subscribe(fn: Subscriber): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

function scheduleRender(): void {
  if (renderScheduled) return;
  renderScheduled = true;
  // requestAnimationFrame coalesces bursts of setState calls into one paint.
  requestAnimationFrame(() => {
    renderScheduled = false;
    for (const fn of subscribers) {
      fn(state);
    }
  });
}

/** Force a synchronous render (used by tests; production code should use setState). */
export function renderNow(): void {
  for (const fn of subscribers) {
    fn(state);
  }
}

// --- High-level state mutations ----------------------------------------------

/**
 * Load a map (with its tokens) and adopt its persisted viewport as the
 * current view. Subsequent pan/zoom is debounced and saved back via
 * `saveViewportDebounced`.
 */
export async function loadMap(id: number): Promise<void> {
  const { map, tokens } = await api.getMap(id);
  setState({
    currentMap: map,
    tokens,
    viewport: { x: map.viewport_x, y: map.viewport_y, zoom: map.zoom },
  });
}

let viewportSaveTimer: ReturnType<typeof setTimeout> | null = null;
const VIEWPORT_DEBOUNCE_MS = 500;

/** Schedule a debounced PUT /api/maps/:id/viewport. */
export function saveViewportDebounced(viewport: Viewport): void {
  const map = state.currentMap;
  if (map === null) return;

  if (viewportSaveTimer !== null) {
    clearTimeout(viewportSaveTimer);
  }
  viewportSaveTimer = setTimeout(() => {
    viewportSaveTimer = null;
    void api.updateViewport(map.id, viewport).catch((err) => {
      console.error('Failed to save viewport', err);
    });
  }, VIEWPORT_DEBOUNCE_MS);
}

/** Replace the token list with an updated entry (or insert if new). */
export function upsertToken(token: Token): void {
  const tokens = state.tokens.slice();
  const idx = tokens.findIndex((t) => t.id === token.id);
  const isNew = idx < 0;
  if (idx >= 0) tokens[idx] = token;
  else tokens.push(token);
  setState({ tokens });

  // A brand-new token created mid-encounter joins the order (pinned to end).
  const map = state.currentMap;
  if (isNew && map?.encounter != null) {
    applyEncounter(enc.addTokenToEncounter(map.encounter, token.id));
  }
}

export function removeToken(id: number): void {
  setState({ tokens: state.tokens.filter((t) => t.id !== id) });

  const map = state.currentMap;
  if (map?.encounter != null) {
    applyEncounter(enc.reconcile(map.encounter, state.tokens.map((t) => t.id)));
  }
}

export function addMapToList(map: MapRecord): void {
  setState({
    maps: [{ id: map.id, name: map.name, created_at: map.created_at }, ...state.maps],
  });
}

export function removeMapFromList(id: number): void {
  setState({ maps: state.maps.filter((m) => m.id !== id) });
}

/**
 * Apply a fresh MapRecord to both `currentMap` (if it's the active one) and
 * the matching entry in the `maps` summary list. Used by rename so the toolbar
 * label and the map selector dropdown update together.
 */
export function updateMapMeta(map: MapRecord): void {
  const patch: Partial<AppState> = {};
  if (state.currentMap !== null && state.currentMap.id === map.id) {
    patch.currentMap = map;
  }
  patch.maps = state.maps.map((m) =>
    m.id === map.id ? { id: map.id, name: map.name, created_at: map.created_at } : m,
  );
  setState(patch);
}

// --- Overlay (walls/doors) mutations -----------------------------------------

let overlaySaveTimer: ReturnType<typeof setTimeout> | null = null;
const OVERLAY_DEBOUNCE_MS = 500;

export function setEditMode(mode: EditMode): void {
  if (mode === 'tokens') {
    setState({ editMode: mode, overlaySubMode: 'walls' });
  } else {
    setState({ editMode: mode });
  }
}

/**
 * Replace a single cell in the current map's overlay (null deletes the cell)
 * and schedule a debounced PUT to /api/maps/:id/overlay.
 *
 * No-op when no map is loaded.
 */
export function updateOverlayCell(x: number, y: number, cell: OverlayCell | null): void {
  const map = state.currentMap;
  if (map === null) return;
  const next: MapOverlay = { ...(map.overlay ?? {}) };
  const key = `${x},${y}`;
  if (cell === null) {
    delete next[key];
  } else {
    next[key] = cell;
  }
  setState({ currentMap: { ...map, overlay: next } });
  saveOverlayDebounced(next);
}

/** Replace the entire overlay (used by drag-paint that batches many cells). */
export function setOverlay(overlay: MapOverlay): void {
  const map = state.currentMap;
  if (map === null) return;
  setState({ currentMap: { ...map, overlay } });
  saveOverlayDebounced(overlay);
}

function saveOverlayDebounced(overlay: MapOverlay): void {
  const map = state.currentMap;
  if (map === null) return;
  const mapId = map.id;
  if (overlaySaveTimer !== null) clearTimeout(overlaySaveTimer);
  overlaySaveTimer = setTimeout(() => {
    overlaySaveTimer = null;
    void api.updateOverlay(mapId, overlay).catch((err) => {
      console.error('Failed to save overlay', err);
    });
  }, OVERLAY_DEBOUNCE_MS);
}

/** Force-flush the pending overlay save (used when switching maps or signing out). */
export function flushOverlaySave(): void {
  if (overlaySaveTimer === null) return;
  clearTimeout(overlaySaveTimer);
  overlaySaveTimer = null;
  const map = state.currentMap;
  if (map === null) return;
  void api.updateOverlay(map.id, map.overlay ?? {}).catch((err) => {
    console.error('Failed to flush overlay save', err);
  });
}

// --- Explored (fog memory) --------------------------------------------------

let exploredSaveTimer: ReturnType<typeof setTimeout> | null = null;
const EXPLORED_DEBOUNCE_MS = 500;

/**
 * Union the given cells into the current map's explored set and trigger a
 * debounced save. No-op when no map is loaded or when the set already
 * contains every cell (avoids spamming saves during idle frames).
 */
export function markExploredCells(cells: Set<string> | Iterable<string>): void {
  const map = state.currentMap;
  if (map === null) return;
  const current = map.explored ?? {};
  let added = false;
  const next: ExploredCells = { ...current };
  for (const key of cells) {
    if (next[key] !== true) {
      next[key] = true;
      added = true;
    }
  }
  if (!added) return;
  setState({ currentMap: { ...map, explored: next } });
  saveExploredDebounced(next);
}

/** Replace the explored set wholesale (used by reset). */
export function setExplored(explored: ExploredCells): void {
  const map = state.currentMap;
  if (map === null) return;
  setState({ currentMap: { ...map, explored } });
  saveExploredDebounced(explored);
}

/** Clear the explored set immediately and persist (no debounce). */
export function resetExplored(): void {
  const map = state.currentMap;
  if (map === null) return;
  if (exploredSaveTimer !== null) {
    clearTimeout(exploredSaveTimer);
    exploredSaveTimer = null;
  }
  setState({ currentMap: { ...map, explored: {} } });
  void api.updateExplored(map.id, {}).catch((err) => {
    console.error('Failed to reset explored', err);
  });
}

function saveExploredDebounced(explored: ExploredCells): void {
  const map = state.currentMap;
  if (map === null) return;
  const mapId = map.id;
  if (exploredSaveTimer !== null) clearTimeout(exploredSaveTimer);
  exploredSaveTimer = setTimeout(() => {
    exploredSaveTimer = null;
    void api.updateExplored(mapId, explored).catch((err) => {
      console.error('Failed to save explored', err);
    });
  }, EXPLORED_DEBOUNCE_MS);
}

/** Force-flush the pending explored save. */
export function flushExploredSave(): void {
  if (exploredSaveTimer === null) return;
  clearTimeout(exploredSaveTimer);
  exploredSaveTimer = null;
  const map = state.currentMap;
  if (map === null) return;
  void api.updateExplored(map.id, map.explored ?? {}).catch((err) => {
    console.error('Failed to flush explored save', err);
  });
}

// --- Initiative encounter ----------------------------------------------------

let encounterSaveTimer: ReturnType<typeof setTimeout> | null = null;
const ENCOUNTER_DEBOUNCE_MS = 500;

/**
 * Write a new encounter state onto the current map and persist it (debounced).
 * Passing null clears the encounter and flushes immediately so a quick reload
 * can't resurrect a finished encounter.
 */
function applyEncounter(encounter: EncounterState | null): void {
  const map = state.currentMap;
  if (map === null) return;
  setState({ currentMap: { ...map, encounter } });

  if (encounterSaveTimer !== null) {
    clearTimeout(encounterSaveTimer);
    encounterSaveTimer = null;
  }
  const mapId = map.id;
  if (encounter === null) {
    void api.updateEncounter(mapId, null).catch((err) => {
      console.error('Failed to clear encounter', err);
    });
    return;
  }
  encounterSaveTimer = setTimeout(() => {
    encounterSaveTimer = null;
    void api.updateEncounter(mapId, encounter).catch((err) => {
      console.error('Failed to save encounter', err);
    });
  }, ENCOUNTER_DEBOUNCE_MS);
}

export function startEncounter(): void {
  const map = state.currentMap;
  if (map === null) return;
  applyEncounter(enc.startEncounter(state.tokens.map((t) => t.id)));
}

export function endEncounter(): void {
  if (state.currentMap?.encounter == null) return;
  applyEncounter(null);
}

export function encounterNewRound(): void {
  const map = state.currentMap;
  if (map?.encounter == null) return;
  applyEncounter(enc.newRound(map.encounter, state.tokens.map((t) => t.id)));
}

export function encounterNext(): void {
  const map = state.currentMap;
  if (map?.encounter == null) return;
  applyEncounter(enc.next(map.encounter));
}

export function encounterPrev(): void {
  const map = state.currentMap;
  if (map?.encounter == null) return;
  applyEncounter(enc.prev(map.encounter));
}

/** The token whose turn it is, or null. Convenience for pan-on-select. */
export function activeEncounterToken(): Token | null {
  const e = state.currentMap?.encounter;
  if (e == null || e.activeTokenId === null) return null;
  return state.tokens.find((t) => t.id === e.activeTokenId) ?? null;
}

/** Test-only reset. */
export function _resetForTesting(): void {
  state = {
    user: null,
    csrfToken: null,
    maps: [],
    currentMap: null,
    tokens: [],
    viewport: { x: 0, y: 0, zoom: 1 },
    draggingTokenId: null,
    editMode: 'tokens',
    overlaySubMode: 'walls',
  };
  subscribers.clear();
  renderScheduled = false;
}
